import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { DataType, DataTypeComponent } from '../../../data-type-component';
import { FieldConfigDTO, MoneyDTO } from '../../../../../../system/system-dtos';
import { MoneyService } from '../money.service';
import { CurrencyDTO } from '../../../lottomart/model/lottomart-model';
import { BlastV2Service } from '../../../../../views/main-table-v2/service/blast-v2.service';
import { CurrencyService } from '../../../../../../common/services/currency.service';

@Component({
  selector: 'app-form-field-money',
  templateUrl: './money-form-field.component.html',
  styleUrls: ['./money-form-field.component.scss'],
  standalone: false,
})
export class MoneyFormFieldComponent extends DataTypeComponent implements OnDestroy, OnInit, OnChanges {
  @Input() formGroup: UntypedFormGroup;

  @Input() data: MoneyDTO;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() editMode: boolean;

  @Input() onlyPositive = false;

  symbol: string;

  displayMoneyValue: string = '';

  previousInternalFormGroupValue: any;

  internalFormGroup: UntypedFormGroup;

  moneyLookupFieldConfig: FieldConfigDTO;

  mainRegex: RegExp = /^-?\d*$/;

  smallRegex: RegExp = /^\d{2}$/;

  editReadOnly: boolean;

  numberOfDecimalPlaces = 2;

  constructor(
    private moneyService: MoneyService,
    private domSanitizer: DomSanitizer,
    private changeDetectorRef: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private blastService: BlastV2Service,
    private currencyService: CurrencyService
  ) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data) {
      this.initialize(changes.data.currentValue);
    }
  }

  transform(unicode: string): SafeHtml {
    if (unicode === undefined) {
      return '';
    }
    return this.domSanitizer.bypassSecurityTrustHtml(unicode);
  }

  ngOnDestroy() {}

  ngOnInit(): void {
    if (this.onlyPositive) {
      this.mainRegex = /^\d*$/;
    }
    if (this.formGroup) {
      const formControl = this.formGroup.get(this.fieldConfig.name);
      formControl.valueChanges.subscribe((d) => {
        if (!this.areEqual(d, this.data)) {
          this.initialize(d);
        }
      });
      const data = this.formGroup.getRawValue();
      this.editReadOnly = this.formGroup.get(this.fieldConfig.name).disabled;
    }
    this.initialize(this.formGroup ? this.formGroup.get(this.fieldConfig.name).value : this.data);
  }

  private areEqual(a: MoneyDTO, b: MoneyDTO): boolean {
    if ((a && !b) || (!a && b) || (!a.currency && b.currency) || (a.currency && !b.currency)) {
      return false;
    }
    return a.currency.code === b.currency.code && a.value === b.value;
  }

  private initialize(d: MoneyDTO) {
    this.data = d;
    if (!this.data) {
      this.data = {
        currency: this.currencyService.getCurrencyForCurrentCohort(),
        value: null,
      };
    }

    this.moneyLookupFieldConfig = {
      label: null,
      name: 'currency',
      type: DataType.lookup,
      lookup: 'currency',
      selectUrl: '/currency',
    };
    if (this.data) {
      if (this.data.currency || (this.data as any).currencyCode) {
        if (!this.data.currency) {
          this.data.currency = this.blastService
            .getCollection('currency')
            .find((c) => c.code === (this.data as any).currencyCode);
        }

        if (this.data !== undefined && this.data.currency !== undefined) {
          this.symbol = this.data.currency.symbol;
        }
      }
      try {
        if (this.data.currency !== undefined && this.data.currency.decimalPlaces !== undefined) {
          this.numberOfDecimalPlaces = this.data.currency.decimalPlaces;
          this.smallRegex = new RegExp(`^[0-9]{${this.numberOfDecimalPlaces}}$`);
        }
      } catch (err) {
        // ignore
      }

      this.displayMoneyValue = this.moneyService.formatMoney(this.data, this.fieldConfig.moneyFormat);
      this.changeDetectorRef.detectChanges();
    }

    let valueMain = this.getValueMain();

    valueMain = this.data.value > -100 && this.data.value < 0 ? '-0' : valueMain;
    const valueSmall = this.data.value ? `0${String(Math.abs(this.data.value % 100))}`.slice(-2) : null;
    this.internalFormGroup = this.formBuilder.group({
      currency: this.data.currency,
      valueMain,
      valueSmall,
    });
    this.previousInternalFormGroupValue = this.internalFormGroup.getRawValue();

    this.internalFormGroup.valueChanges.subscribe((d) => {
      this.setParentFormError();
      let { valueSmall } = d;

      if (d.valueSmall && String(d.valueSmall).length > this.numberOfDecimalPlaces) {
        valueSmall = String(d.valueSmall).slice(0, this.numberOfDecimalPlaces);
        this.internalFormGroup.get('valueSmall').setValue(valueSmall, { emitEvent: false });
        return;
      }
      if (d.valueSmall && String(d.valueSmall).length < this.numberOfDecimalPlaces) {
        const tmpSmallValue = String(d.valueSmall);
        valueSmall =
          tmpSmallValue.length > this.numberOfDecimalPlaces
            ? tmpSmallValue.slice(-this.numberOfDecimalPlaces)
            : tmpSmallValue;
        this.internalFormGroup.get('valueSmall').setValue(valueSmall, { emitEvent: false });
        return;
      }

      if ((d.valueMain && !this.mainRegex.test(d.valueMain)) || (valueSmall && !this.smallRegex.test(valueSmall))) {
        this.internalFormGroup.setValue(this.previousInternalFormGroupValue, { emitEvent: false });
        return;
      }

      // let moneyValue = Math.abs(Number(d.valueMain ? d.valueMain : 0) * 100) + Number(valueSmall ? valueSmall : 0);
      let moneyValue =
        Math.abs(Number(d.valueMain ? d.valueMain : 0) * 10 ** this.numberOfDecimalPlaces) + Number(valueSmall || 0);
      moneyValue = d.valueMain && d.valueMain.startsWith('-') ? -moneyValue : moneyValue;

      if (Number.isNaN(moneyValue)) {
        return;
      }

      const value: MoneyDTO = {
        currency: d.currency,
        value: moneyValue,
      };
      this.data = value;
      this.formGroup.get(this.fieldConfig.name).setValue(value);
      this.previousInternalFormGroupValue = this.internalFormGroup.getRawValue();
      this.formGroup.get(this.fieldConfig.name).setErrors(null);
    });

    if (!this.internalFormGroup.getRawValue().currency) {
      this.internalFormGroup.setValue({
        currency: this.currencyService.getCurrencyForCurrentCohort(),
        valueMain,
        valueSmall,
      });
    }
  }

  private setParentFormError() {
    this.formGroup.get(this.fieldConfig.name).setErrors({ incorrect: true });
  }

  private getValueMain() {
    if (!this.data.value) {
      return null;
    }

    const notRounded = this.data.value / 10 ** this.numberOfDecimalPlaces;
    // const notRounded = this.data.value / 100;
    return String(notRounded >= 0 ? Math.floor(notRounded) : Math.ceil(notRounded));
  }

  shouldDisplaySmallUnit() {
    const { currency } = this.internalFormGroup.getRawValue();
    return currency && currency.decimalPlaces && currency.decimalPlaces >= 2;
  }
}
